import React, { type FC, useContext, type PropsWithChildren } from 'react';

import {
  Breadcrumb,
  Heading,
  Box,
  Collection,
  LayoutContext,
  Text,
  Link,
  Container,
  Image,
  VisibleVideo,
  CtaWithSSO,
  reactHTMLParser,
} from '@lucid/components';
import { withImage } from '@lucid/hooks';
import { getColor } from '@lucid/theme';

import type { Lucid } from 'types/types';
import circle from 'assets/images/freehand-circle.svg';
import underline from 'assets/images/freehand-underline.svg';
import arrow from 'assets/images/freehand-arrow.svg';

interface HeroProps {
  header: string | string[];
  headerColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  subheaderColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  circledHeaderPiece?: string;
  subheader?: string;
  hasSSO?: boolean;
  ssoSeparatorColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  image?: Lucid.Directus.Generated.Directus_Files;
  backgroundImage?: string;
  smallBgImage?: boolean;
  breadCrumbHub?: string;
  breadCrumbPage?: string;
  pageType?: string;
  buttons?: Lucid.Directus.Generated.Links[];
  stack?: boolean;
  phoneCta?: Record<string, string> | null;
  hasNarrowHeadings?: boolean;
}

const renderButtons = (buttons: Lucid.Directus.Generated.Links[]) =>
  buttons?.map((button, i) => {
    if (button.final_url) {
      return (
        <Link
          key={`${button.default_link_text}-hero-cta=${i}` || `hero-button-link-text-${i}`}
          displayAsButton
          href={button.final_url}
          variant={i === 0 ? 'primary' : 'secondary'}
        >
          {button.default_link_text}
        </Link>
      );
    }
  });

const Hero: FC<PropsWithChildren<HeroProps>> = ({
  header,
  headerColor,
  circledHeaderPiece,
  subheader,
  subheaderColor,
  hasSSO,
  ssoSeparatorColor,
  image,
  backgroundImage,
  breadCrumbHub,
  breadCrumbPage,
  pageType,
  buttons,
  hasNarrowHeadings,
}) => {
  const { shortCode } = useContext(LayoutContext);
  const hasImage = image?.filename_disk;
  const isVideo = image?.type?.includes('video');

  return (
    <Box as="section" css={theme => ({ label: `${pageType}`, paddingTop: theme.space.s80 })}>
      <Container
        css={theme => ({
          overflow: 'hidden',
          label: 'Container',
          [theme.breakpoints.bp900.min]: {
            display: 'flex',
          },
        })}
        extraPadding={3}
      >
        {breadCrumbHub && (
          <Breadcrumb
            items={[
              <Link
                key={breadCrumbHub}
                href={breadCrumbHub !== 'Solutions' ? `/solutions/${breadCrumbHub.toLowerCase()}` : '/solutions'}
                variant="secondary"
              >
                {breadCrumbHub}
              </Link>,
              breadCrumbPage,
            ]}
          />
        )}
        <Box
          css={theme => ({
            label: 'textContent',
            maxWidth: hasNarrowHeadings ? '640px' : '780px',
            margin: '0 auto',
            [theme.breakpoints.bp900.min]: [
              { margin: `auto ${theme.space.s24}px auto 0`, flex: '1' },
              hasImage && { maxWidth: '440px' },
              !hasImage && { margin: '0 auto' },
            ],
          })}
        >
          <Heading
            as="h1"
            css={theme => ({
              color: getColor({
                color: headerColor,
                theme,
              }),
              textAlign: 'center',
              marginBottom: 0,
              [theme.breakpoints.bp0.min]: {
                marginBottom: theme.space.s32,
              },
              [theme.breakpoints.bp900.min]: [
                {
                  textAlign: 'left',
                  marginBottom: theme.space.s40,
                },
                !hasImage && { textAlign: 'center' },
              ],
            })}
            dataElementPageLocation="hero"
            variant="hero3XL700"
          >
            {header && Array.isArray(header) ? reactHTMLParser(header.join('<br/>')) : header}
            {circledHeaderPiece && (
              <>
                {' '}
                <span css={{ position: 'relative' }}>
                  {circledHeaderPiece}
                  {circledHeaderPiece.length > 7 || shortCode === 'ja' ? (
                    <Image
                      css={theme => ({
                        position: 'absolute',
                        right: 0,
                        bottom: '-15px',
                        zIndex: theme.zIndices[0],
                        maxWidth: 'none',
                        [theme.breakpoints.bp768.min]: {
                          width: '100%',
                        },
                      })}
                      dataElementPageLocation="hero"
                      imageAlt=""
                      imageSrc={underline}
                    />
                  ) : (
                    <Image
                      css={theme => ({
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        height: '60px',
                        [theme.breakpoints.bp768.min]: {
                          right: '-10px',
                          top: 0,
                          left: 'auto',
                          transform: 'none',
                        },
                      })}
                      dataElementPageLocation="hero"
                      imageAlt=""
                      imageSrc={circle}
                    />
                  )}
                  <Image
                    css={theme => [
                      circledHeaderPiece.length > 7 || shortCode === 'ja'
                        ? {
                            position: 'absolute',
                            top: '55%',
                            maxWidth: 'none',
                            left: 'calc(100% + 35px)',
                            [theme.breakpoints.bp500.min]: {
                              maxWidth: '15vw',
                            },
                            [theme.breakpoints.bp900.min]: {
                              top: '25%',
                            },
                          }
                        : {
                            position: 'absolute',
                            top: '25%',
                            left: 'calc(100% + 35px)',
                            maxWidth: 'none',
                          },
                    ]}
                    dataElementPageLocation="hero"
                    imageAlt=""
                    imageSrc={arrow}
                  />
                </span>
              </>
            )}
          </Heading>
          <Box
            css={theme => ({
              maxWidth: hasNarrowHeadings ? '350px' : '580px',
              margin: '0 auto',
              [theme.breakpoints.bp900.min]: [
                { margin: 0 },
                !hasImage && {
                  margin: '0 auto',
                },
              ],
            })}
          >
            {subheader && (
              <Text
                css={theme => ({
                  color: getColor({
                    color: subheaderColor,
                    theme,
                  }),
                  textAlign: 'center',
                  [theme.breakpoints.bp900.min]: [
                    { textAlign: 'left' },
                    !hasImage && {
                      textAlign: 'center',
                    },
                  ],
                })}
                dataElementPageLocation="hero"
                variant="labelS400"
              >
                {reactHTMLParser(subheader, { replaceOptions: { replaceTag: 'span' } })}
              </Text>
            )}
          </Box>
          {hasSSO && (
            <CtaWithSSO
              css={theme => ({
                margin: `${theme.space.s40}px auto`,
                [theme.breakpoints.bp900.min]: { margin: `${theme.space.s40}px 0` },
              })}
              linkText={buttons?.[0]?.default_link_text}
              linkUrl={buttons?.[0]?.final_url}
              ssoSeparatorColor={ssoSeparatorColor}
            />
          )}
          {buttons && !hasSSO && (
            <>
              <Box css={theme => ({ [theme.breakpoints.bp900.min]: { display: 'none' } })}>
                <Collection
                  css={{
                    '& > div': {
                      alignItems: 'center',
                    },
                  }}
                  justifyContent="center"
                  justifyInnerItem="center"
                  mx={3}
                  my={3}
                >
                  {renderButtons(buttons)}
                </Collection>
              </Box>
              <Box
                css={theme => ({
                  display: 'none',
                  [theme.breakpoints.bp900.min]: {
                    display: 'initial',
                  },
                })}
              >
                <Collection
                  css={{
                    label: 'buttons',
                    '& > div': {
                      alignItems: 'center',
                    },
                  }}
                  justifyContent={hasImage ? 'start' : 'center'}
                  justifyInnerItem={hasImage ? 'start' : 'center'}
                  mx={3}
                  my={3}
                >
                  {renderButtons(buttons)}
                </Collection>
              </Box>
            </>
          )}
        </Box>
        {image && (
          <Box
            css={theme => ({
              label: 'image',
              width: 0,
              margin: `${theme.space.s40}px auto 0`,
              textAlign: 'center',
              [theme.breakpoints.bp900.min]: {
                maxWidth: '580px',
                margin: 'auto 0 auto auto',
                flex: '1',
              },
            })}
          >
            {isVideo ? (
              <VisibleVideo
                description={image.translations?.[0]?.alt || ''}
                descriptionElementId="hero-video-description"
                src={withImage(image)}
              />
            ) : (
              <Image
                css={theme => ({
                  maxWidth: '580px',
                  transform: 'translateX(-50%)',
                  [theme.breakpoints.bp900.min]: {
                    maxWidth: '100%',
                    transform: 'none',
                  },
                })}
                dataElementPageLocation="hero"
                imageAlt={image.translations?.[0]?.alt || ''}
                imageSrc={withImage(image)}
              />
            )}
            {backgroundImage && (
              <Image
                css={theme => ({
                  height: '100%',
                  maxHeight: '480px',
                  objectFit: 'contain',
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                  zIndex: theme.zIndices[0],
                  [theme.breakpoints.bp768.min]: {
                    right: '10%',
                    bottom: 'auto',
                    top: 0,
                  },
                })}
                dataElementPageLocation="hero"
                imageAlt=""
                imageSrc={backgroundImage}
              />
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Hero;
